import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, Trash2, Crown, Pin, ChevronLeft, Send, Tag } from "lucide-react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function CommunityPost({ scope = "community" }) {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const myAlias = user?.public_alias;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const apiPrefix = scope === "internal" ? "/internal" : "/community";
  const backLink = scope === "internal" ? "/internal" : "/community";

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`${apiPrefix}/posts/${postId}`);
      setData(r.data);
    } catch (e) {
      console.error("Post load failed", e);
      toast.error("Post not found");
      navigate(backLink);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [postId]);

  const onLike = async () => {
    try {
      const r = await api.post(`${apiPrefix}/posts/${postId}/like`);
      setData(d => ({ ...d, post: { ...d.post, liked_by_me: r.data.liked, like_count: r.data.like_count } }));
    } catch (e) { console.error(e); }
  };

  const onPin = async () => {
    try {
      const r = await api.post(`${apiPrefix}/posts/${postId}/pin`);
      setData(d => ({ ...d, post: { ...d.post, pinned: r.data.pinned } }));
    } catch (e) { console.error(e); toast.error("Failed to pin"); }
  };

  const onDeletePost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`${apiPrefix}/posts/${postId}`);
      toast.success("Deleted");
      navigate(backLink);
    } catch (e) { console.error(e); toast.error("Failed to delete"); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`${apiPrefix}/posts/${postId}/comments`, { body: comment.trim() });
      setData(d => ({ ...d, comments: [...d.comments, r.data] }));
      setComment("");
    } catch (err) {
      console.error("Comment failed", err);
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const onDeleteComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`${apiPrefix}/comments/${id}`);
      setData(d => ({ ...d, comments: d.comments.filter(c => c.id !== id) }));
    } catch (e) { console.error(e); toast.error("Failed"); }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center text-white/50">Loading…</div>
      </div>
    );
  }

  const post = data.post;

  return (
    <div className="min-h-screen bg-fc-navy text-white flex" data-testid={`${scope}-post-detail`}>
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-white/10 bg-[#060A24]/70 backdrop-blur sticky top-0 z-30">
          <Link to={backLink} className="flex items-center gap-1 text-xs tracking-[0.18em] uppercase font-bold text-white/55 hover:text-fc-red">
            <ChevronLeft size={14} /> Back
          </Link>
        </div>

        <div className="px-6 lg:px-10 py-8 space-y-6 max-w-3xl mx-auto">
          <article className="bg-[#060A24] border border-white/10 p-6 sm:p-8">
            <header className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-fc-red/15 text-fc-red flex items-center justify-center font-mono font-bold text-sm">
                  {post.author?.alias?.slice(-4)}
                </div>
                <div>
                  <p className="font-mono text-fc-red font-bold">
                    {post.author?.alias}
                    {post.author?.alias === myAlias && <span className="ml-2 text-[10px] text-white/45 tracking-[0.18em]">YOU</span>}
                  </p>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-white/45">
                    <Crown size={9} className="inline mr-1" /> {post.author?.rank}
                  </p>
                </div>
              </div>
              {post.pinned && <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#F59E0B] border border-[#F59E0B]/40 px-2 py-1 flex items-center gap-1"><Pin size={10} /> Pinned</span>}
            </header>

            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-4">{post.title}</h1>
            <div className="text-white/85 text-base leading-relaxed prose-fc" dangerouslySetInnerHTML={{ __html: post.body }} />

            {post.course_tag && (
              <Link to={`/courses/${post.course_tag}`} className="inline-flex items-center gap-1 mt-4 text-[10px] tracking-[0.18em] uppercase font-bold text-[#3B82F6] hover:underline">
                <Tag size={10} /> {post.course_title || post.course_tag}
              </Link>
            )}

            <footer className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
              <button onClick={onLike} className={`flex items-center gap-1.5 text-sm font-bold tracking-wider ${post.liked_by_me ? "text-fc-red" : "text-white/55 hover:text-white"}`} data-testid="detail-like">
                <Heart size={16} fill={post.liked_by_me ? "currentColor" : "none"} /> {post.like_count}
              </button>
              <div className="flex items-center gap-3">
                {isAdmin && scope === "community" && (
                  <button onClick={onPin} className="text-white/55 hover:text-[#F59E0B]" title="Pin/unpin" data-testid="detail-pin"><Pin size={15} /></button>
                )}
                {(post.author?.alias === myAlias || isAdmin) && (
                  <button onClick={onDeletePost} className="text-white/55 hover:text-fc-red" data-testid="detail-delete"><Trash2 size={15} /></button>
                )}
              </div>
            </footer>
          </article>

          {/* Composer */}
          <form onSubmit={submitComment} className="bg-[#060A24] border border-white/10 p-5 space-y-3" data-testid="comment-composer">
            <p className="fc-overline text-[10px]">REPLY</p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add to the conversation. Mention with @Dealer-XXXX."
              rows={3}
              maxLength={4000}
              className="w-full bg-fc-navy border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-fc-red resize-none"
              data-testid="comment-body"
            />
            <div className="flex justify-end">
              <button disabled={busy || !comment.trim()} className="btn-fc-red text-xs px-4 py-2" data-testid="comment-submit">
                <Send size={12} /> {busy ? "Sending…" : "Reply"}
              </button>
            </div>
          </form>

          {/* Comments */}
          <div className="space-y-3">
            {data.comments.length === 0 ? (
              <p className="text-white/55 text-sm">Be first to reply.</p>
            ) : (
              data.comments.map(c => (
                <div key={c.id} className="bg-[#060A24] border border-white/10 p-4" data-testid={`comment-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-fc-red font-bold text-xs">{c.author?.alias}</span>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-white/40"><Crown size={9} className="inline mr-1" />{c.author?.rank}</span>
                    </div>
                    {(c.author?.alias === myAlias || isAdmin) && (
                      <button onClick={() => onDeleteComment(c.id)} className="text-white/40 hover:text-fc-red" title="Delete">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div className="text-white/80 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: c.body }} />
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
